/**
 * Settings data access and the pure rules behind the settings screen.
 *
 * Every mutation goes through the API: the functions here only build the
 * request, and the caller renders whatever problem+json comes back.
 */

export type JsonFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

export interface LocalizedText {
  az: string;
  en: string;
  ru: string;
}

export interface AuditActor {
  id: string;
  role?: string;
}

export type SectionId =
  | 'modules'
  | 'rules'
  | 'dimensions'
  | 'numbering'
  | 'approval'
  | 'users'
  | 'packages'
  | 'audit'
  | 'documentTypes'
  | 'chains'
  | 'printTemplates';

export interface SectionResult<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

export interface SettingsData {
  modules: SectionResult<readonly ModuleView[]>;
  rules: SectionResult<readonly PostingRuleRow[]>;
  trial: SectionResult<readonly PostingRuleRow[]>;
  dimensions: SectionResult<readonly DimensionSlot[]>;
  numbering: SectionResult<readonly NumberingRow[]>;
  approval: SectionResult<readonly ApprovalRouteRow[]>;
  users: SectionResult<readonly UserRow[]>;
  packages: SectionResult<readonly PackageInfo[]>;
  audit: SectionResult<readonly AuditRow[]>;
  documentTypes: SectionResult<readonly DocumentTypeRow[]>;
  chains: SectionResult<readonly ChainRow[]>;
  printTemplates: SectionResult<readonly PrintTemplateRow[]>;
}

export const EMPTY_SECTION: SectionResult<never> = { loading: false, error: null, data: null };

/* ----------------------------------------------------------------- modules */

export type ModuleStatus = 'not_installed' | 'installed' | 'active' | 'readonly' | 'disabled';

export interface ModuleDependencyView {
  code: string;
  constraint: string;
  status: string;
  satisfied: boolean;
}

export interface ModuleView {
  code: string;
  nameI18n: LocalizedText;
  version: string;
  ownerTeam?: string;
  status: ModuleStatus;
  requires: readonly string[];
  disablePolicy: string;
  dependents: readonly { code: string; status: ModuleStatus }[];
  dependencies?: readonly ModuleDependencyView[];
}

export type ModuleAction = 'activate' | 'readonly' | 'disable';

export interface ModuleVerdict {
  allowed: boolean;
  reasonKey?: string;
}

/**
 * Mirrors the kernel's refusals so the button is already disabled with the
 * reason visible; the server re-checks and its problem text wins.
 */
export function canModuleAction(module: Pick<ModuleView, 'status' | 'disablePolicy'>, action: ModuleAction): ModuleVerdict {
  if (action === 'activate') {
    if (module.status === 'active') return { allowed: false, reasonKey: 'settings.modules.alreadyActive' };
    return { allowed: true };
  }
  if (action === 'readonly') {
    if (module.status === 'readonly') return { allowed: false, reasonKey: 'settings.modules.alreadyReadonly' };
    if (module.status !== 'active') return { allowed: false, reasonKey: 'settings.modules.notActive' };
    return { allowed: true };
  }
  if (module.disablePolicy === 'never') {
    return { allowed: false, reasonKey: 'settings.modules.kernelProtected' };
  }
  if (module.status !== 'active' && module.status !== 'readonly') {
    return { allowed: false, reasonKey: 'settings.modules.notActive' };
  }
  return { allowed: true };
}

export function moduleName(module: ModuleView, language: 'az' | 'en' | 'ru'): string {
  return module.nameI18n[language];
}

/* -------------------------------------------------------------- rule trial */

export interface PostingRuleRow {
  id: string;
  code: string;
  description: string;
  debitAccount: string;
  creditAccount: string;
  status: 'draft' | 'approved' | 'retired';
}

export type TrialStatus = 'unchanged' | 'changed' | 'new' | 'removed';

export interface RuleTrialRow {
  code: string;
  live: string;
  trial: string;
  difference: string;
  status: TrialStatus;
}

function rulePair(rule: PostingRuleRow): string {
  return `${rule.debitAccount} → ${rule.creditAccount}`;
}

/** Side-by-side comparison only: the live rules are never mutated or written. */
export function trialDiff(
  live: readonly PostingRuleRow[],
  trial: readonly PostingRuleRow[],
): RuleTrialRow[] {
  const liveByCode = new Map(live.map((rule) => [rule.code, rule]));
  const rows: RuleTrialRow[] = [];
  for (const next of trial) {
    const current = liveByCode.get(next.code);
    if (current === undefined) {
      rows.push({ code: next.code, live: '—', trial: rulePair(next), difference: '+1', status: 'new' });
      continue;
    }
    liveByCode.delete(next.code);
    const same = rulePair(current) === rulePair(next);
    rows.push({
      code: next.code,
      live: rulePair(current),
      trial: rulePair(next),
      difference: same ? '—' : '1',
      status: same ? 'unchanged' : 'changed',
    });
  }
  for (const stale of liveByCode.values()) {
    rows.push({ code: stale.code, live: rulePair(stale), trial: '—', difference: '−1', status: 'removed' });
  }
  return rows;
}

/* -------------------------------------------------------------- dimensions */

export type DimensionPolicy = 'required' | 'optional' | 'turnover_only' | 'forbidden';

export interface DimensionSlot {
  slot: number;
  dimType: string;
  policy: DimensionPolicy;
}

export const DIMENSION_SLOT_COUNT = 8;

/** spec §4.6: exactly eight typed slots; an unconfigured slot is `forbidden`. */
export function normalizeDimensionSlots(slots: readonly DimensionSlot[]): DimensionSlot[] {
  const bySlot = new Map(slots.map((slot) => [slot.slot, slot]));
  const result: DimensionSlot[] = [];
  for (let slot = 1; slot <= DIMENSION_SLOT_COUNT; slot += 1) {
    result.push(bySlot.get(slot) ?? { slot, dimType: '', policy: 'forbidden' });
  }
  return result;
}

/* --------------------------------------------------------------- numbering */

export interface NumberingRow {
  documentType: string;
  prefix: string;
  next: number;
  reset: 'never' | 'yearly' | 'monthly';
}

/* --------------------------------------------------------------- approvals */

export interface ApprovalStepRow {
  approvers: readonly string[];
  deadlineHours: number;
  substituteId?: string;
}

export interface ApprovalRouteRow {
  id: string;
  documentType: string;
  steps: readonly ApprovalStepRow[];
  condition?: Record<string, unknown>;
}

/** Flattens the steps into the «mühasib → baş mühasib» chain the mockup draws. */
export function routeChain(route: Pick<ApprovalRouteRow, 'steps'>): string[] {
  return route.steps.flatMap((step) => step.approvers.map(String));
}

/* -------------------------------------------------------- users and roles */

export const ROLES = ['admin', 'chief_accountant', 'accountant'] as const;

export type RoleCode = (typeof ROLES)[number];

export interface UserRow {
  id: string;
  email: string;
  fullName: string;
  roles: readonly string[];
  isActive: boolean;
  locale?: string;
}

export interface RoleAction {
  key: string;
  allowed: Record<RoleCode, boolean>;
}

/** spec §8 / AD-10: three roles, fixed; the server is the authority. */
export const ROLE_MATRIX: readonly RoleAction[] = [
  { key: 'document.create', allowed: { admin: true, chief_accountant: true, accountant: true } },
  { key: 'document.post', allowed: { admin: true, chief_accountant: true, accountant: true } },
  { key: 'document.cancel', allowed: { admin: true, chief_accountant: true, accountant: false } },
  { key: 'rules.write', allowed: { admin: true, chief_accountant: true, accountant: false } },
  { key: 'period.close', allowed: { admin: true, chief_accountant: true, accountant: false } },
  { key: 'users.manage', allowed: { admin: true, chief_accountant: false, accountant: false } },
  { key: 'audit.read', allowed: { admin: true, chief_accountant: true, accountant: false } },
];

/* ---------------------------------------------------------------- packages */

export interface PackageInfo {
  code: string;
  version: string;
  installedAt?: string;
  current: boolean;
  stats?: Record<string, number>;
  history?: readonly { version: string; date: string; change: string }[];
}

export type MergeChoice = 'keep' | 'take' | 'merge';

export interface PackageConflict {
  key: string;
  current: string;
  incoming: string;
}

export interface MergeResult {
  key: string;
  value: string;
}

/** spec §4.8: one decision per conflicting key; `merge` takes the edited text. */
export function applyMerge(
  conflicts: readonly PackageConflict[],
  choices: Readonly<Record<string, MergeChoice>>,
  merged: Readonly<Record<string, string>> = {},
): MergeResult[] {
  return conflicts.map((conflict) => {
    const choice = choices[conflict.key] ?? 'keep';
    if (choice === 'take') return { key: conflict.key, value: conflict.incoming };
    if (choice === 'merge') return { key: conflict.key, value: merged[conflict.key] ?? conflict.incoming };
    return { key: conflict.key, value: conflict.current };
  });
}

/* ------------------------------------------------------------------- audit */

export interface AuditRow {
  id: string;
  at: string;
  actor: string;
  action: string;
  entity: string;
  entityId?: string;
  reason?: string;
}

/* ----------------------------------------------------------- document types */

export interface FieldRow {
  name: string;
  type: string;
  required?: boolean;
}

export interface StatusTransitionRow {
  from: string;
  to: string;
  operation: string;
}

export interface DocumentTypeRow {
  code: string;
  module: string;
  nameI18n: LocalizedText;
  operations: readonly { code: string; nameI18n: LocalizedText }[];
  statusModel: {
    statuses: readonly string[];
    initial: string;
    transitions: readonly StatusTransitionRow[];
    approvalEnabled: boolean;
  };
  approvalRoute?: string | null;
  batchCapable?: boolean;
  fields: readonly FieldRow[];
  tableFields: { fields: readonly FieldRow[] };
}

export interface TypeSummary {
  headerFields: string[];
  tableFields: string[];
  approvalEnabled: boolean;
  statusPath: string;
}

export function typeSummary(type: Pick<DocumentTypeRow, 'fields' | 'tableFields' | 'statusModel'>): TypeSummary {
  return {
    headerFields: type.fields.map((field) => field.name),
    tableFields: type.tableFields.fields.map((field) => field.name),
    approvalEnabled: type.statusModel.approvalEnabled,
    statusPath: type.statusModel.statuses.join(' → '),
  };
}

/* ----------------------------------------------------------- document chains */

export interface FieldMapEntry {
  from: string;
  to: string;
}

export interface CompletionRuleRow {
  metric: 'remaining_qty' | 'remaining_amount';
  operator: '<=';
  value: string;
}

export interface ChainRow {
  id: string;
  sourceType: string;
  sourceOperation: string;
  targetType: string;
  targetOperation: string;
  fieldMap: readonly FieldMapEntry[];
  autoCreate: boolean;
  required: boolean;
  qtyMode: '1:1' | '1:N' | 'N:1';
  qtyControl: boolean;
  completionRule?: CompletionRuleRow;
  isDisabled: boolean;
}

export interface ChainInput {
  sourceType: string;
  sourceOperation: string;
  targetType: string;
  targetOperation: string;
  fieldMap: readonly FieldMapEntry[];
  autoCreate: boolean;
  required: boolean;
  qtyMode: '1:1' | '1:N' | 'N:1';
  qtyControl: boolean;
}

export function readPath(source: Record<string, unknown>, path: string): unknown {
  let cursor: unknown = source;
  for (const segment of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/** Shows the child document the values it will receive before the rule is saved. */
export function previewFieldMap(
  source: Record<string, unknown>,
  entries: readonly FieldMapEntry[],
): { to: string; value: string }[] {
  return entries.map((entry) => {
    const value = readPath(source, entry.from);
    return {
      to: entry.to,
      value: value === undefined || value === null || value === '' ? '—' : String(value),
    };
  });
}

/* --------------------------------------------------------- print templates */

export interface PrintBlockRow {
  type: string;
  text?: string;
  label?: string;
  value?: string;
}

export interface PrintTemplateRow {
  id: string;
  documentType: string;
  nameI18n: LocalizedText;
  locale: 'az' | 'en' | 'ru';
  isDefault: boolean;
  layout: { blocks: readonly PrintBlockRow[] };
}

export function templatePreview(template: Pick<PrintTemplateRow, 'layout'>): string[] {
  return template.layout.blocks.map((block) => {
    if (block.type === 'title') return `title: ${block.text ?? ''}`;
    if (block.type === 'field') return `field: ${block.label ?? ''} = ${block.value ?? ''}`;
    if (block.type === 'signature') return `signature: ${block.label ?? ''}`;
    return `text: ${block.text ?? ''}`;
  });
}

/* ---------------------------------------------------------------- requests */

export const ENDPOINTS = {
  modules: '/modules',
  rules: '/posting-rules',
  dimensions: '/dimensions',
  numbering: '/numbering',
  approval: '/approval-routes',
  users: '/users',
  packages: '/config-packages',
  audit: '/audit',
  documentTypes: '/document-types',
  chains: '/document-chains',
  printTemplates: '/print-templates',
} as const;

export async function loadModules(fetchJson: JsonFetch): Promise<ModuleView[]> {
  return fetchJson<ModuleView[]>(ENDPOINTS.modules);
}

export async function setModuleStatus(
  fetchJson: JsonFetch,
  code: string,
  action: ModuleAction,
  body: { actor: AuditActor; reason?: string },
): Promise<ModuleView> {
  return fetchJson<ModuleView>(`${ENDPOINTS.modules}/${encodeURIComponent(code)}/${action}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function loadRules(fetchJson: JsonFetch): Promise<PostingRuleRow[]> {
  return fetchJson<PostingRuleRow[]>(ENDPOINTS.rules);
}

/** spec §6: the preview returns the candidate rules without writing the journal. */
export async function previewRules(fetchJson: JsonFetch, rules: readonly PostingRuleRow[]): Promise<PostingRuleRow[]> {
  return fetchJson<PostingRuleRow[]>(`${ENDPOINTS.rules}/preview`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ rules }),
  });
}

export async function loadDimensions(fetchJson: JsonFetch): Promise<DimensionSlot[]> {
  return normalizeDimensionSlots(await fetchJson<DimensionSlot[]>(ENDPOINTS.dimensions));
}

export async function loadNumbering(fetchJson: JsonFetch): Promise<NumberingRow[]> {
  return fetchJson<NumberingRow[]>(ENDPOINTS.numbering);
}

export async function loadApprovalRoutes(fetchJson: JsonFetch): Promise<ApprovalRouteRow[]> {
  return fetchJson<ApprovalRouteRow[]>(ENDPOINTS.approval);
}

export async function createApprovalRoute(
  fetchJson: JsonFetch,
  input: { documentType: string; steps: ApprovalStepRow[] },
): Promise<ApprovalRouteRow> {
  return fetchJson<ApprovalRouteRow>(ENDPOINTS.approval, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function loadUsers(fetchJson: JsonFetch): Promise<UserRow[]> {
  return fetchJson<UserRow[]>(ENDPOINTS.users);
}

export async function assignRoles(fetchJson: JsonFetch, userId: string, roles: readonly RoleCode[]): Promise<UserRow> {
  return fetchJson<UserRow>(`${ENDPOINTS.users}/${encodeURIComponent(userId)}/roles`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ roles }),
  });
}

export async function loadPackages(fetchJson: JsonFetch): Promise<PackageInfo[]> {
  return fetchJson<PackageInfo[]>(ENDPOINTS.packages);
}

export async function previewPackageUpdate(
  fetchJson: JsonFetch,
  code: string,
): Promise<{ conflicts: PackageConflict[] }> {
  return fetchJson<{ conflicts: PackageConflict[] }>(
    `${ENDPOINTS.packages}/${encodeURIComponent(code)}/update/preview`,
    { method: 'POST' },
  );
}

export async function applyPackageUpdate(
  fetchJson: JsonFetch,
  code: string,
  decisions: MergeResult[],
): Promise<{ applied: number }> {
  return fetchJson<{ applied: number }>(`${ENDPOINTS.packages}/${encodeURIComponent(code)}/update`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ decisions }),
  });
}

export async function loadAudit(fetchJson: JsonFetch): Promise<AuditRow[]> {
  return fetchJson<AuditRow[]>(ENDPOINTS.audit);
}

export async function loadDocumentTypes(fetchJson: JsonFetch): Promise<DocumentTypeRow[]> {
  return fetchJson<DocumentTypeRow[]>(ENDPOINTS.documentTypes);
}

export async function patchDocumentType(
  fetchJson: JsonFetch,
  code: string,
  patch: { fields?: FieldRow[]; tableFields?: { fields: FieldRow[] }; approvalEnabled?: boolean },
): Promise<DocumentTypeRow> {
  return fetchJson<DocumentTypeRow>(`${ENDPOINTS.documentTypes}/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export async function loadChains(fetchJson: JsonFetch): Promise<ChainRow[]> {
  return fetchJson<ChainRow[]>(ENDPOINTS.chains);
}

export async function createChain(fetchJson: JsonFetch, input: ChainInput): Promise<ChainRow> {
  return fetchJson<ChainRow>(ENDPOINTS.chains, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function loadPrintTemplates(fetchJson: JsonFetch): Promise<PrintTemplateRow[]> {
  return fetchJson<PrintTemplateRow[]>(ENDPOINTS.printTemplates);
}

export async function createPrintTemplate(
  fetchJson: JsonFetch,
  input: { documentType: string; nameI18n: LocalizedText; locale: PrintTemplateRow['locale']; layout: PrintTemplateRow['layout'] },
): Promise<PrintTemplateRow> {
  return fetchJson<PrintTemplateRow>(ENDPOINTS.printTemplates, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/* ---------------------------------------------------------------------------
 * Wire shapes for the mounted screens.
 *
 * These mirror the platform controllers one-to-one. Money never appears here
 * as a JS number: amounts travel as decimal strings (AD-8).
 * ------------------------------------------------------------------------ */

export { createJsonFetch, problemMessage, readProblem } from '../accounts/accounts.api';
export type { ScreenProblem } from '../accounts/accounts.api';

export interface ModuleRefusal {
  code: string;
  detail: string;
}

export interface ModuleActionContext {
  actor: AuditActor & { name?: string };
  reason?: string;
}

export interface Dimension {
  slot: number;
  code: string;
  nameI18n: LocalizedText;
}

export interface RuleLine {
  side: 'D' | 'C';
  account: string;
  amount: string;
}

export interface PostingRule {
  code: string;
  documentType: string;
  operationKind: string;
  version: number | string;
  expression: { lines: readonly RuleLine[] };
}

export interface RulePreview {
  balanced: boolean;
  difference: string;
}

/** Trial runner handed to the posting-rules view. */
export type RulePreviewer = (rule: PostingRule) => Promise<RulePreview>;

export interface RuleTrialChange {
  index: number;
  before?: RuleLine;
  after?: RuleLine;
  changed: boolean;
}

function sameLine(left: RuleLine | undefined, right: RuleLine | undefined): boolean {
  if (left === undefined || right === undefined) return left === right;
  return left.side === right.side && left.account === right.account && left.amount === right.amount;
}

/** Line-by-line diff of a rule and its trial version. Nothing is written. */
export function ruleTrial(before: PostingRule, after: PostingRule): RuleTrialChange[] {
  const length = Math.max(before.expression.lines.length, after.expression.lines.length);
  const rows: RuleTrialChange[] = [];
  for (let index = 0; index < length; index += 1) {
    const previous = before.expression.lines[index];
    const next = after.expression.lines[index];
    rows.push({
      index,
      ...(previous === undefined ? {} : { before: previous }),
      ...(next === undefined ? {} : { after: next }),
      changed: !sameLine(previous, next),
    });
  }
  return rows;
}

export interface NumberingRule {
  typeCode: string;
  prefix: string;
  padding: number;
  resetPeriod: 'never' | 'yearly' | 'monthly';
  nextNumber: number;
}

export interface ApprovalStep {
  role?: string;
  userId?: string;
  deadlineHours?: number;
}

export interface ApprovalRoute {
  id: string;
  documentType: string;
  steps: readonly ApprovalStep[];
}

export type UserRecord = UserRow;

export interface ConfigPackageSetting {
  key: string;
  scope: string;
  scopeId?: string | null;
  value: unknown;
  version: number;
}

export interface ConfigPackage {
  name: string;
  format: string;
  generatedAt: string;
  settings: readonly ConfigPackageSetting[];
}

export interface MergePreview {
  conflicts: number;
  changes: number;
}

export interface AuditEntry {
  id: string;
  at: string;
  actor: string;
  action: string;
  entity: string;
  oldValue?: string;
  newValue?: string;
}

/** POST /modules/:code/{action} — the server owns the lifecycle rules (spec §4.2). */
export async function moduleAction(
  fetchJson: JsonFetch,
  code: string,
  action: ModuleAction,
  context: ModuleActionContext,
): Promise<ModuleView> {
  return fetchJson<ModuleView>(`${ENDPOINTS.modules}/${encodeURIComponent(code)}/${action}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(context),
  });
}

/** POST /settings/import?dryRun=true — reports conflicts and changes, applies nothing. */
export async function previewImport(fetchJson: JsonFetch, pkg: ConfigPackage): Promise<MergePreview> {
  return fetchJson<MergePreview>('/settings/import?dryRun=true', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(pkg),
  });
}

export type DocumentTypeDefinition = DocumentTypeRow;

export async function listDocumentTypes(fetchJson: JsonFetch): Promise<DocumentTypeDefinition[]> {
  return fetchJson<DocumentTypeDefinition[]>(ENDPOINTS.documentTypes);
}

export type ChainRule = ChainRow & {
  sourceStatusCondition?: readonly string[];
  activeFrom?: string;
  activeTo?: string;
};

export async function listChains(fetchJson: JsonFetch): Promise<ChainRule[]> {
  return fetchJson<ChainRule[]>(ENDPOINTS.chains);
}

export async function deleteChain(fetchJson: JsonFetch, id: string): Promise<void> {
  await fetchJson<unknown>(`${ENDPOINTS.chains}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type PrintTemplate = PrintTemplateRow;

export async function listPrintTemplates(fetchJson: JsonFetch): Promise<PrintTemplate[]> {
  return fetchJson<PrintTemplate[]>(ENDPOINTS.printTemplates);
}
